import { useEffect, useMemo, useState } from "react";
import type { Person } from "../../models/types";
import {
  buildCousinsKidsIdentifyRounds,
  canonicalPersonIds,
  shuffledBirthOrderChallenge,
  type CousinsKidsIdentifyRound,
} from "../../family/birthOrder";
import { birthOrderRevealLine } from "../../utils/personDisplay";
import { hapticError, hapticMedium, hapticSuccess } from "../../utils/haptics";
import { Avatar } from "../shared/Avatar";
import { IconCheck, IconX } from "../shared/Icons";

type Phase = "identify" | "identifyReveal" | "order" | "finished";

export function CousinsKidsGame({
  people,
  onDone,
  onExit,
}: {
  people: Person[];
  onDone: (score: number, total: number, startedAt: number) => void;
  onExit: () => void;
}) {
  const bundlePeople = people;
  const bundle = useMemo(
    () => ({ people: bundlePeople, meta: { sibling_order: [] as string[] } }),
    [bundlePeople],
  );

  const [rounds] = useState(() =>
    buildCousinsKidsIdentifyRounds({
      ...bundle,
      meta: { name: "", version: 0, root_ids: [], sibling_order: [] },
      map_pins: [],
      grandparent_gallery: [],
      recent_events: [],
      learn_decks: [],
      trivia_questions: [],
    }),
  );
  const canonicalIds = useMemo(
    () => canonicalPersonIds(
      {
        ...bundle,
        meta: { name: "", version: 0, root_ids: [], sibling_order: [] },
        map_pins: [],
        grandparent_gallery: [],
        recent_events: [],
        learn_decks: [],
        trivia_questions: [],
      },
      "kids",
    ),
    [bundle],
  );

  const [identifyIndex, setIdentifyIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>("identify");
  const [selectedCoupleId, setSelectedCoupleId] = useState<string | null>(null);
  const [selectedNameId, setSelectedNameId] = useState<string | null>(null);
  const [parentScore, setParentScore] = useState(0);
  const [nameScore, setNameScore] = useState(0);
  const [orderedPeople, setOrderedPeople] = useState<Person[]>(() =>
    shuffledBirthOrderChallenge(
      {
        people: bundlePeople,
        meta: { name: "", version: 0, root_ids: [], sibling_order: [] },
        map_pins: [],
        grandparent_gallery: [],
        recent_events: [],
        learn_decks: [],
        trivia_questions: [],
      },
      "kids",
    ),
  );
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [startedAt] = useState(Date.now());
  const [reported, setReported] = useState(false);

  const kidCount = rounds.length;
  const currentRound: CousinsKidsIdentifyRound | undefined = rounds[identifyIndex];
  const orderScore = orderedPeople.filter((p, i) => p.person_id === canonicalIds[i]).length;
  const combinedScore = nameScore + parentScore + orderScore;
  const combinedTotal = kidCount * 3;

  useEffect(() => {
    if (phase !== "finished" || reported) return;
    setReported(true);
    onDone(combinedScore, combinedTotal, startedAt);
  }, [phase, reported, combinedScore, combinedTotal, startedAt, onDone]);

  if (rounds.length < 2) {
    return (
      <div className="pad-screen stack">
        <h2 className="title-serif">No cousins&apos; kids to quiz yet</h2>
        <p className="muted">Kid data will show up here after the next family bundle export.</p>
        <button type="button" className="btn-primary" onClick={onExit}>
          Back
        </button>
      </div>
    );
  }

  const lockIn = () => {
    if (!currentRound || !selectedCoupleId || !selectedNameId) return;
    const parentsCorrect = selectedCoupleId === currentRound.correctCoupleId;
    const nameCorrect = selectedNameId === currentRound.kidId;
    if (parentsCorrect) setParentScore((s) => s + 1);
    if (nameCorrect) setNameScore((s) => s + 1);
    if (parentsCorrect && nameCorrect) hapticSuccess();
    else hapticError();
    setPhase("identifyReveal");
  };

  const advanceReveal = () => {
    if (identifyIndex + 1 >= kidCount) {
      setPhase("order");
      return;
    }
    setIdentifyIndex((i) => i + 1);
    setSelectedCoupleId(null);
    setSelectedNameId(null);
    setPhase("identify");
  };

  const submitOrder = () => {
    setOrderSubmitted(true);
    hapticMedium();
    setPhase("finished");
  };

  if (phase === "finished") {
    return <p className="muted pad-screen">Wrapping up…</p>;
  }

  if (phase === "identify" || phase === "identifyReveal") {
    return (
      <IdentifyPhase
        round={currentRound}
        phase={phase}
        identifyIndex={identifyIndex}
        kidCount={kidCount}
        parentScore={parentScore}
        nameScore={nameScore}
        people={bundlePeople}
        selectedCoupleId={selectedCoupleId}
        selectedNameId={selectedNameId}
        onSelectCouple={setSelectedCoupleId}
        onSelectName={setSelectedNameId}
        onLockIn={lockIn}
        onAdvance={advanceReveal}
      />
    );
  }

  return (
    <div className="pad-screen stack game-flow">
      <p className="muted">
        Oldest at the top, youngest at the bottom. Each correct spot earns a point.
      </p>
      <div className="game-status">
        <strong>Age order</strong>
        <span className="gold-text">
          ID {nameScore + parentScore}/{kidCount * 2}
        </span>
      </div>
      {orderedPeople.map((person, index) => {
        const isCorrect = orderSubmitted ? person.person_id === canonicalIds[index] : null;
        return (
          <div
            key={person.person_id}
            className={`birth-order-row ${isCorrect === true ? "correct" : ""} ${isCorrect === false ? "wrong" : ""}`}
          >
            <span className="rank-num">{index + 1}</span>
            <Avatar person={person} size={44} />
            <div style={{ flex: 1 }}>
              <strong>{person.display_name}</strong>
              {orderSubmitted && birthOrderRevealLine(person) ? (
                <div className="muted">{birthOrderRevealLine(person)}</div>
              ) : null}
            </div>
            {!orderSubmitted ? (
              <>
                <button type="button" className="btn-ghost" aria-label="Move up" onClick={() => move(index, -1)}>↑</button>
                <button type="button" className="btn-ghost" aria-label="Move down" onClick={() => move(index, 1)}>↓</button>
              </>
            ) : isCorrect ? (
              <IconCheck className="result-icon success" />
            ) : (
              <IconX className="result-icon review" />
            )}
          </div>
        );
      })}
      {!orderSubmitted ? (
        <button type="button" className="btn-primary" onClick={submitOrder}>
          Check my order
        </button>
      ) : null}
    </div>
  );

  function move(from: number, delta: number) {
    const to = from + delta;
    if (to < 0 || to >= orderedPeople.length) return;
    setOrderedPeople((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }
}

function IdentifyPhase({
  round,
  phase,
  identifyIndex,
  kidCount,
  parentScore,
  nameScore,
  people,
  selectedCoupleId,
  selectedNameId,
  onSelectCouple,
  onSelectName,
  onLockIn,
  onAdvance,
}: {
  round?: CousinsKidsIdentifyRound;
  phase: Phase;
  identifyIndex: number;
  kidCount: number;
  parentScore: number;
  nameScore: number;
  people: Person[];
  selectedCoupleId: string | null;
  selectedNameId: string | null;
  onSelectCouple: (id: string) => void;
  onSelectName: (id: string) => void;
  onLockIn: () => void;
  onAdvance: () => void;
}) {
  if (!round) return null;
  const kid = people.find((p) => p.person_id === round.kidId);

  if (phase === "identifyReveal") {
    const parentsCorrect = selectedCoupleId === round.correctCoupleId;
    const nameCorrect = selectedNameId === round.kidId;
    const correctCouple = round.coupleChoices.find((c) => c.id === round.correctCoupleId);
    return (
      <div className="pad-screen stack game-flow">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${((identifyIndex + 0.5) / kidCount) * 100}%` }} />
        </div>
        <div className="card pad-screen stack" style={{ alignItems: "center", textAlign: "center" }}>
          {kid ? <Avatar person={kid} size={96} ring /> : null}
          <h2 className="title-serif">{kid?.display_name}</h2>
          {correctCouple ? <p className="gold-text">{correctCouple.label}</p> : null}
          <div className="reveal-chips">
            <span className={parentsCorrect ? "chip success" : "chip review"}>
              Parents {parentsCorrect ? "✓" : "✗"}
            </span>
            <span className={nameCorrect ? "chip success" : "chip review"}>
              Name {nameCorrect ? "✓" : "✗"}
            </span>
          </div>
        </div>
        <button type="button" className="btn-primary" onClick={onAdvance}>
          {identifyIndex + 1 >= kidCount ? "Sort by age" : "Next kid"}
        </button>
      </div>
    );
  }

  return (
    <div className="pad-screen stack game-flow">
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${(identifyIndex / kidCount) * 100}%` }} />
      </div>
      <div className="game-status">
        <div>
          <div className="muted caps">Kid {identifyIndex + 1} of {kidCount}</div>
          <strong>Parents & name</strong>
        </div>
        <div className="gold-text">{nameScore + parentScore}/{kidCount * 2} ID points</div>
      </div>
      <div className="card pad-screen stack" style={{ alignItems: "center" }}>
        {kid ? <Avatar person={kid} size={112} ring /> : null}
        <h2 className="title-serif">Who is this?</h2>
      </div>
      <p className="muted">Whose kid is this, and what&apos;s their name?</p>
      <div className="caps muted">Parents</div>
      {round.coupleChoices.map((couple) => (
        <button
          key={couple.id}
          type="button"
          className={`choice-btn ${selectedCoupleId === couple.id ? "selected" : ""}`}
          onClick={() => onSelectCouple(couple.id)}
        >
          {couple.label}
        </button>
      ))}
      <div className="caps muted">Name</div>
      {round.nameChoiceIds.map((id) => {
        const p = people.find((x) => x.person_id === id);
        if (!p) return null;
        return (
          <button
            key={id}
            type="button"
            className={`choice-btn ${selectedNameId === id ? "selected" : ""}`}
            onClick={() => onSelectName(id)}
          >
            {p.display_name}
          </button>
        );
      })}
      <button
        type="button"
        className="btn-primary"
        disabled={!selectedCoupleId || !selectedNameId}
        onClick={onLockIn}
      >
        Lock in
      </button>
    </div>
  );
}
